import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import {
	addHomeMetricWidgets,
	type HomeSession,
} from '@databox/ember-integrations/lib/components/Home/addHomeMetricWidgets';
import { AiTrackingOrigin, ChatPromptMode } from '@databox/types/domains/ai/chat.enums';
import type { HomeMetricWidgetSeed } from '@databox/types/domains/home/home';
import Cookies from 'js-cookie';
import moment from 'moment';

import type { RecordData, Service, SourcesMap } from 'webapp-types';
import {
	AccountType,
	BillingFeature,
	DeliveryType,
	ScheduleType,
	SourceTypeCategory,
	TeamSectionItemType,
	TemplateObjectType,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { serializeMetricSetupData, sortBy, uniqBy } from 'webapp/utils/common';
import { buildHomeSession, toReactMetricSetup } from 'webapp/utils/home';

import type { BoardRecord, ReportRecord, ScorecardRecord } from 'webapp/models';
import { type Board, type Source, type SourceType, type Template } from 'webapp/models-custom';
import MetricSetup from 'webapp/models-custom/metric-setup';
import WizardSecondTour from 'webapp/models-custom/tour/wizard-second';
import type DataLoaderMain from 'webapp/data-loader/main';

import { type onConnectSourceResult } from 'webapp/services/connect';
import { type TeamSectionItemSnapshot } from 'webapp/services/model-manager/team-sections';

export const enum Step {
	WELCOME = 'welcome',
	ACCOUNT_SETUP = 'account-setup',
	INTEGRATIONS = 'integrations',
	DBOX_INTRO = 'dbox-intro',
	FINAL = 'final',
	GENIE_EXP = 'genie-exp',
}

export const MARKETPLACE_SKILL_COOKIE = 'website_marketplace_skill';
const MARKETPLACE_SKILL_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export default class OnboardingService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('tour') declare tourService: Service.Tour;
	@service('connect') declare connectService: Service.Connect;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('billing') declare billingService: Service.Billing;
	@service('experiment/genie-onboarding-920261')
	declare experimentGenieOnboarding920261Service: Service.Experiment.GenieOnboarding920261;

	@tracked _currentStep: Step | null = null;

	get currentStep(): Step {
		return this._currentStep || this.steps[0]!.id;
	}

	set currentStep(step: Step) {
		this._currentStep = step;
	}

	get isOwnerOnboarding() {
		return this.currentUserService.isOwner;
	}

	get isUserOnboarding() {
		return !this.isOwnerOnboarding;
	}

	@cached
	get steps() {
		const steps = [];

		if (!this.userSettingsService.hasSeeOnboardingWelcome) {
			steps.push({ id: Step.WELCOME });
		}

		// genie-exp asks only attribution + account type here; the final step installs the skills.
		if (this.isEligibleForMarketplaceSkillFlow) {
			steps.push({ id: Step.GENIE_EXP });
			steps.push({ id: Step.FINAL });
			return steps;
		}

		// Onboarding experiment (920261): the scripted Genie chat replaces every
		// remaining step for eligible users. See services/experiment/genie-onboarding-920261.ts.
		const { isEligibleForFlowB, isEligibleForFlowC } =
			this.experimentGenieOnboarding920261Service;
		if (isEligibleForFlowC || isEligibleForFlowB) {
			steps.push({ id: Step.GENIE_EXP });
			return steps;
		}

		if (this.isOwnerOnboarding) {
			if (
				!this.accountService.metadata.employees &&
				!this.accountService.hasPartnerResellerId
			) {
				steps.push({ id: Step.ACCOUNT_SETUP });
			}
			if (!this.userSettingsService.isHomeAttributionQuestionClosed && !this.templateId) {
				steps.push({ id: Step.GENIE_EXP });
			}
			steps.push({ id: Step.INTEGRATIONS });
		} else {
			steps.push({ id: Step.DBOX_INTRO });
		}

		steps.push({ id: Step.FINAL });

		return steps;
	}

	nextStep(): void {
		this.currentStep = this.steps[this.getCurrentStepIndex() + 1]!.id;
	}

	previousStep(): void {
		this.currentStep = this.steps[this.getCurrentStepIndex() - 1]!.id;
	}

	private getCurrentStepIndex(): number {
		return this.steps.findIndex((step) => step.id === this.currentStep);
	}

	startOnboarding() {
		this.recordMarketplaceSkillSignup();
		this.navigationService.replaceRoute('onboarding');
	}

	// ------- Common methods ------

	saveConnectSelections(sourceTypeIds: number[]) {
		this.requestService
			.api('POST', '/connect/selection', sourceTypeIds)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	getSourcesMap(source: Source, sourceType: SourceType): SourcesMap {
		const sourceId = source.id!,
			sourceTypeId = sourceType.id!,
			sourcesMap = { [sourceTypeId]: sourceId };

		return sourcesMap;
	}

	getMetricSetupsFromBoard(boards: Board[]): MetricSetup[] {
		const metricSetups: MetricSetup[] = [];

		boards.forEach((board) => {
			board.blockObjs.map((block) => {
				(block.blockSetup?.dataSelectsShown || []).forEach((ds) => {
					const metricSetup = ds.metricSetup;
					if (metricSetup?.isFilledSingleOutput) {
						metricSetups.push(metricSetup);
					}
				});
			});
		});

		return uniqBy(metricSetups, 'key').slice(0, 3);
	}

	private createFromTemplate(
		source: Source,
		sourceType: SourceType,
		objectType: TemplateObjectType = TemplateObjectType.BOARD,
	) {
		let mostPopularPublicTemplateBySourceType: Template | null = null;
		const isReport = objectType === TemplateObjectType.REPORT,
			sourcesMap = this.getSourcesMap(source, sourceType),
			publicTemplate = this.publicTemplate;

		if (!this.getSourceTypesFromTemplate().includes(sourceType.key)) {
			const publicTemplates = sourceType.publicTemplates.filter(
				(template) => template.objectType === objectType,
			);

			mostPopularPublicTemplateBySourceType = this.mmTemplatesService.createModel(
				sortBy(publicTemplates, 'usedCount').reverse()[0]!,
			);
		}

		const afterUse = (record: BoardRecord | ReportRecord, boards?: RecordData.Board.Raw[]) => {
			this.clearSafeGuardTimeout();

			if (isReport) {
				const reportModel = this.mmReportsService.findModel((record as ReportRecord).id!)!;
				reportModel.favorite = true;
				void reportModel.save();
			} else {
				const boardModel = this.mmBoardsService.createModelFromRecord(
					record as BoardRecord,
				);
				boardModel.favorite = true;
				void boardModel.save();
			}

			const boardsModels = isReport
				? (boards || []).map((board) => this.mmBoardsService.findModel(String(board.id))!)
				: [this.mmBoardsService.createModelFromRecord(record as BoardRecord)];

			if (boardsModels.length) {
				void Promise.allSettled(
					boardsModels.map((board) => {
						return this.mmBoardsService.requestBoardData(board.id!, true);
					}),
				).then(() => {
					const metricSetups = this.getMetricSetupsFromBoard(boardsModels);

					void this.addMetricsToHome(metricSetups);
					this.createScorecardForSelectedMetrics(metricSetups, source);
				});
			}

			this.markCompleted();
			this.redirectToGenie();
		};

		const firstPublicTemplate = mostPopularPublicTemplateBySourceType || publicTemplate;

		if (isReport) {
			this.mmReportsService.createReportFromTemplate(
				firstPublicTemplate!,
				this.origin,
				afterUse,
				undefined,
				sourcesMap,
				true,
				{
					isInfolayerShown: false,
					isTrackingDisabled: true,
				},
			);
		} else {
			this.mmBoardsService.createBoardFromTemplate(
				firstPublicTemplate!,
				this.origin,
				afterUse,
				undefined,
				sourcesMap,
				true,
				{
					isInfolayerShown: false,
					isTrackingDisabled: true,
				},
			);
		}

		// In case we connected source that is not on public template
		// from which we made signup, we make also board from this
		// public template
		if (mostPopularPublicTemplateBySourceType && publicTemplate) {
			void this.mmTemplatesService
				.useTemplate(publicTemplate, { dataSourceTypeMap: {} })
				.catch(() => {
					// nothing
				});

			// Add selected sources for possible multiple unresolved source types
			// on public template
			this.saveConnectSelections(
				publicTemplate?.sourcesUsedList?.map((item) => item.value as number) || [],
			);
		}
	}

	createBoardFromTemplate(source: Source, sourceType: SourceType) {
		this.createFromTemplate(source, sourceType, TemplateObjectType.BOARD);
	}

	createReportFromTemplate(source: Source, sourceType: SourceType) {
		this.createFromTemplate(source, sourceType, TemplateObjectType.REPORT);
	}

	// React posts the widgets in this order and owns the payload and the tracking.
	async addMetricsToHome(metricSetups: MetricSetup[]): Promise<void> {
		const seeds: HomeMetricWidgetSeed[] = [];

		for (const metricSetup of metricSetups) {
			const data = serializeMetricSetupData(metricSetup.metricSetupData);
			const reactMetricSetup = data ? toReactMetricSetup(data) : null;
			if (!reactMetricSetup) {
				continue;
			}

			seeds.push({ metricSetup: reactMetricSetup, title: metricSetup.metric?.name ?? null });
		}

		if (!seeds.length) {
			return;
		}

		const session = this.homeSession;
		if (!session) {
			return;
		}

		try {
			await addHomeMetricWidgets(session, seeds);
		} catch (error) {
			// React resolves per-widget failures itself; anything that still escapes must not break onboarding.
			this.loggerService.logError('[ONBOARDING] Home widgets failed', {
				error: JSON.stringify(error),
			});
		}
	}

	private get homeSession(): HomeSession | null {
		const session = buildHomeSession(
			this.currentUserService,
			this.accountService,
			AiTrackingOrigin.ONBOARDING,
		);
		if (!session) {
			this.loggerService.logError('[ONBOARDING] Home widgets skipped: no session');
		}
		return session;
	}

	createScorecardForSelectedMetrics(metricSetups: MetricSetup[], source: Source): void {
		// don't create if sharing&notifications not enabled
		if (!this.billingService.sharingEnabled) {
			return;
		}

		const scheduleHourOfDay = moment().hour();
		const scheduleDayOfWeek = [moment().day()];

		const scorecard = this.mmScorecardsService.createNewModel({
			title: 'WEEKLY SCORECARD',
			scheduleType: ScheduleType.WEEKLY,
			scheduleHourOfDay,
			scheduleDayOfWeek,
			scheduleDayOfMonth: null,
			delivery: [DeliveryType.EMAIL],
			userIds: [this.currentUserService.numericId!],
		});

		void scorecard.save().then((scorecard: ScorecardRecord) => {
			const scorecardModel = this.mmScorecardsService.findModel(scorecard.id!)!;

			this.analyticsService.event(TrackingEvent.SCORECARD_CREATED, {
				metrics: metricSetups.map((ms) => ms.metric?.name),
				datasources: [source.sourceType.key],
				name: scorecardModel?.title,
				origin: this.origin,
				frequency: scorecardModel.schedule.typeText,
				delivery: scorecardModel.deliveriesTexts,
			});

			metricSetups.forEach((metricSetup, index) => {
				const dataSelect = this.mmScorecardsService.createDataSelectForScorecard(
					scorecardModel,
					metricSetup.metricSetupWithoutFiltersData || undefined,
				);
				dataSelect.order = index;
				void dataSelect.save();
			});
		});
	}

	createTeamFromSelectedMetrics(board?: Board, metricSetups?: MetricSetup[]): void {
		const name = 'Space';
		const doesTeamExist = this.mmTeamsService.teamModels.some(
			(team) => team.name.toLowerCase() === name?.toLowerCase(),
		);

		if (doesTeamExist) {
			return;
		}

		const team = this.mmTeamsService.createNewTeam(name);
		team.save()
			.then((teamRecord) => {
				this.analyticsService.event(TrackingEvent.TEAM_CREATED, {
					type: team.teamTypeForTracking,
					origin: this.origin,
				});

				void this.mmTeamsService.requestSections(teamRecord.id!).then((reply) => {
					const teamSections = typeof reply === 'object' ? reply.teamSections : undefined;
					if (!teamSections?.length) {
						return;
					}

					const sectionId = teamSections[0]!.id;
					const section = this.mmTeamSectionsService.findModel(String(sectionId));

					if (!section) {
						return;
					}

					let promises: Promise<TeamSectionItemSnapshot>[] = [];
					if (metricSetups?.length) {
						promises = metricSetups.map((metricSetup) => {
							return this.mmTeamSectionItemsService
								.preparePayloadForCreate(metricSetup.metricSetupData!)
								.then((objectData) => {
									const item: TeamSectionItemSnapshot = {
										objectType: TeamSectionItemType.METRIC,
										objectData,
									};
									return item;
								});
						});
					}

					if (board) {
						promises.push(
							new Promise((resolve) => {
								const item: TeamSectionItemSnapshot = {
									objectType: TeamSectionItemType.DATABOARD,
									objectId: board.numericId || undefined,
								};
								resolve(item);
							}),
						);
					}

					return Promise.all(promises)
						.then((items: TeamSectionItemSnapshot[]) => {
							if (items.length) {
								void this.mmTeamSectionsService.batchAddItems(section, items, {
									isInfolayerShown: false,
									isTrackingDisabled: true,
								});
							}
						})
						.catch((error) => {
							throw error;
						});
				});
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// ------- Google Sheets / Excel Demo source ------

	excelDemoSourceFile =
		'https://cdn1.databox.com/analytics/onboarding/excel-demo-source-datasets.xlsx';
	excelDemoSourceFileName = 'Demo source';
	excelDatasetName = 'Demo dataset';
	excelSampleDashboardTemplateId = 1033384;
	excelSampleReportTemplateId = 1034534;

	useExcelSampleFile(
		options: {
			skipRedirectAndDoneEvent?: boolean;
		} = {},
	) {
		const { skipRedirectAndDoneEvent = false } = options;
		const { excelDemoSourceFile, excelDemoSourceFileName, excelSampleDashboardTemplateId } =
			this;

		return this.createFile(
			excelDemoSourceFile,
			excelDemoSourceFileName,
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		).then((file) => {
			const sourceType = this.mmSourceTypesService.findModelByKey('ExcelFileUpload');
			const isReportPublicTemplate =
				this.publicTemplate?.objectType === TemplateObjectType.REPORT;

			if (sourceType) {
				void sourceType
					.connectNew({
						file,
						origin: 'onboarding_sample_dashboard_confirmed',
						isSample: true,
					})
					.then((result: onConnectSourceResult) => {
						if (result.isSuccess) {
							const sourceId = result.source.id;

							const dataSourceTypeMap = {
								[sourceType.id!]: sourceId,
							};
							const boardPromise = this.requestService.apiWithAsyncReply(
								'POST',
								`/templates/${excelSampleDashboardTemplateId}`,
								{
									dataSourceTypeMap,
									isSample: true,
								},
							);

							const reportPromise = this.requestService.apiWithAsyncReply(
								'POST',
								`/templates/${this.excelSampleReportTemplateId}`,
								{
									dataSourceTypeMap,
									isSample: true,
								},
							);

							const promises = [boardPromise, reportPromise];

							Promise.all(promises)
								.then((reply) => {
									this.clearSafeGuardTimeout();

									const boardId = String(reply[0]?.boards![0]?.id); // the template reply always carries its boards

									let board;
									if (boardId) {
										board = this.mmBoardsService.findModel(boardId)!;
									} else {
										board = this.mmBoardsService.normalBoardModels[0];
									}

									const reportId = String(reply[1]?.reports![0]!.id); // and reports
									let reportModel;
									if (reportId) {
										reportModel = this.mmReportsService.findModel(reportId)!;
									} else {
										reportModel = this.mmReportsService.normalReportModels[0];
									}

									if (!board || !reportModel) {
										if (!skipRedirectAndDoneEvent) {
											this.redirectToGenie();
											this.markCompleted();
										}
										return;
									}

									if (isReportPublicTemplate) {
										reportModel.favorite = true;
									} else {
										board.favorite = true;
									}

									reportModel.isSample = true;
									board.isSample = true;

									const metricSetups = this.getMetricSetupsFromBoard([board]);

									void this.addMetricsToHome(metricSetups);

									const publicTemplate = this.publicTemplate;
									if (publicTemplate) {
										void this.mmTemplatesService
											.useTemplate(publicTemplate, { dataSourceTypeMap: {} })
											.catch(
												this.requestService.showAllValidationErrors.bind(
													this.requestService,
												),
											);
									}

									const savePromises = [board.save(), reportModel.save()];

									if (!skipRedirectAndDoneEvent) {
										Promise.all(savePromises)
											.then((reply) => {
												this.redirectToGenie();

												if (reply[1]?.id) {
													this.analyticsService.event(
														TrackingEvent.REPORT_CREATED,
														{
															origin: this.origin,
														},
													);
												}
												this.markCompleted();
											})
											.catch((errorData) => {
												this.requestService.showAllValidationErrors(
													errorData,
												);
											});
									}
								})
								.catch((error) => {
									this.requestService.showAllValidationErrors(error);
								});
						}
					});
			}
		});
	}

	private buildOnboardingAnswersPrompt(): string {
		const parts: string[] = [];

		if (this.accountService.accountType === AccountType.REGULAR) {
			parts.push(
				'Q: Are you going to use Databox for one business, or for multiple clients?\nA: One business - my company or team',
			);
		} else if (this.accountService.accountType === AccountType.AGENCY) {
			parts.push(
				"Q: Are you going to use Databox for one business, or for multiple clients?\nA: Multiple clients - I'm an agency",
			);
		}

		const companySize = this.accountService.metadata.employees;
		if (companySize) {
			parts.push(`Q: What is your company size?\nA: ${companySize}`);
		}

		const tools = this.accountService.metadata.sourcesSelection
			.map((key) => this.mmSourceTypesService.findModelByKey(key)?.name)
			.filter((name): name is string => !!name);
		if (tools.length) {
			parts.push(
				`Q: Which tools do you want to pull data from? Type any others below.\nA: ${tools.join(', ')}`,
			);
		}

		return parts.join('\n');
	}

	private redirectToGenie(): void {
		this.aiChatService.openGenieWithOnboardingAnswers(this.buildOnboardingAnswersPrompt());
	}

	private addNewDataset(sourceType: SourceType, source: Source): void {
		this.connectService.addNewDataset(sourceType.key, {
			sourceId: source.numericId!,
			dataSourceName: source.title,
		});
	}

	private async createFile(path: string, name: string, type: string): Promise<File> {
		const response = await fetch(path);
		const data = await response.blob();

		return new File([data], name, { type });
	}

	markCompleted() {
		if (this.templateId) {
			this.markTemplateFlowCompleted();
		} else {
			this.markRegularFlowCompleted();
		}
	}

	private _markCompleted() {
		this.clearSafeGuardTimeout();
		(this.navigationService.dataLoader as DataLoaderMain).markAsOnboarded();
		this.currentUserService.isOnboarded = true;
		this.eventManagerService.trigger(AppEvent.ONBOARDING_COMPLETED);
	}

	// ------- Safe guard timeout ------
	// to ensure user gets into app after the timeout no matter what

	private safeGuardTimeoutDuration = 30;
	private safeguardTimeout: ReturnType<typeof setTimeout> | null = null;

	setSafeGuardTimeout(durationSeconds = this.safeGuardTimeoutDuration) {
		this.safeguardTimeout = setTimeout(() => {
			this.loggerService.logError('[ONBOARDING] Safeguard timeout');
			this.markCompleted();
			this.redirectToGenie();
		}, durationSeconds * 1000);
	}

	armFinalStepSafeGuard() {
		const finalStepSafeGuardDuration = 10;
		if (this.safeguardTimeout || !this.isOwnerOnboarding) {
			return;
		}
		this.setSafeGuardTimeout(finalStepSafeGuardDuration);
	}

	private clearSafeGuardTimeout() {
		if (this.safeguardTimeout) {
			clearTimeout(this.safeguardTimeout);
			this.safeguardTimeout = null;
		}
	}

	// ------- Regular flow ------

	handleConnectedSource(source: Source) {
		const sourceType = source.sourceType;
		if (sourceType.isSqlishConnector) {
			this.addNewDataset(sourceType, source);
			this.markCompleted();
			this.redirectToGenie();
		} else {
			if (this.publicTemplate?.objectType === TemplateObjectType.REPORT) {
				this.createReportFromTemplate(source, sourceType);
			} else {
				this.createBoardFromTemplate(source, sourceType);
			}
		}
	}

	private markRegularFlowCompleted() {
		this.analyticsService.event(TrackingEvent.ONBOARDING_ALL_SET, {
			type: 'regular',
			experiments: [this.experimentGenieOnboarding920261Service.experimentOrigin],
		});

		this._markCompleted();
	}

	// ------- Template flow ------

	private markTemplateFlowCompleted() {
		this._markCompleted();
	}

	triggerEventAllSet(): void {
		this.analyticsService.event(TrackingEvent.ONBOARDING_ALL_SET, { type: 'template' });
	}

	// Template Signup Onboarding Flow (Experiment 2)
	// https://app.asana.com/0/527984279702626/1201723007155784/f

	// Mandatory to connect source for Template signups
	// https://app.asana.com/0/527984279702626/1206009259740410/f

	get templateId(): string | null {
		const templateId = this.userSettingsService.templateId;

		return templateId ? String(templateId) : null;
	}

	get publicTemplate(): Template | null {
		return (
			this.mmTemplatesService.publicTemplateModels.find(
				(item) => item.id === this.templateId,
			) || null
		);
	}

	get isEligibleForOnboarding(): boolean {
		return (
			!this.currentUserService.isOnboarded && this.currentUserService.isSignedUpFromTemplate
		);
	}

	// Designer
	get isEligibleToTransitionToDatabordsPage(): boolean {
		return (
			this.currentUserService.isSignedUpFromTemplate &&
			this.mmBoardsService.normalBoardModels.length < 2
		);
	}

	get designerLogoTooltipText(): string {
		return `Go to ${this.isEligibleToTransitionToDatabordsPage ? 'Dashboards' : 'Home'}`;
	}

	getSourceTypesFromTemplate(): string[] {
		const publicTemplate = this.publicTemplate;

		return publicTemplate ? publicTemplate.specificSourceTypesKeys : [];
	}

	triggerWizardDataboardIntroTourModalOnDesignerExit(): boolean {
		if (this.tourService.isEligibleForWizardDataboardIntroTour) {
			this.triggerWizardDataboardIntroTourModal();
			return true;
		}

		return false;
	}

	// Board preview
	triggerEventOnFirstEditViaBoardPreview(): void {
		this.isEligibleToTriggerActionOnBoardPreviewClose = false;

		if (
			this.mmBoardsService.normalBoardModels.length === 1 &&
			this.tourService.isEligibleForWizardSecondTour
		) {
			// Prevent to show wizard second when TEMPLATE_ONBOARDING_EDIT is triggered
			void this.userSettingsService.save(WizardSecondTour.keyForPersistantDone, true);
			this.analyticsService.event(TrackingEvent.TEMPLATE_ONBOARDING_EDIT);
		}
	}

	triggerEventAllSetAfterFirstBoardCreated(): void {
		if (
			this.mmBoardsService.normalBoardModels.length < 2 &&
			this.tourService.isEligibleForWizardSecondTour
		) {
			this.triggerEventAllSet();
		}
	}

	// Flag indicating when triggerActionOnBoardPreviewClose can be triggered from unloading board preview modal.
	// When pressing edit in board preview modal this method cannot be triggered.
	@tracked isEligibleToTriggerActionOnBoardPreviewClose = true;

	triggerActionOnBoardPreviewClose(): void {
		if (!this.isEligibleToTriggerActionOnBoardPreviewClose) {
			return;
		}

		const boardRecords = this.mmBoardsService.normalBoardModels,
			routeName = 'boards.public-templates';

		if (boardRecords.length < 2 && this.tourService.isEligibleForWizardSecondTour) {
			if (boardRecords.length < 1) {
				this.triggerEventAllSet();
			}

			this.navigationService.transitionToRoute(routeName).then(() => {
				this.triggerWizardSecondModal();
			});
		}

		if (boardRecords.length === 2 && this.tourService.isEligibleForWizardDataboardIntroTour) {
			this.navigationService.transitionToRoute(routeName).then(() => {
				this.triggerWizardDataboardIntroTourModal();
			});
		}
	}

	preventTransitionToBoardsAfterBoardCreated(): boolean {
		return (
			this.currentUserService.isSignedUpFromTemplate &&
			this.tourService.isEligibleForWizardSecondTour
		);
	}

	// Wizard second modal
	triggerAutoScheduleSnapshot(): void {
		const boardId = sortBy(this.mmBoardsService.normalBoardModels, 'created')?.[0]?.id || null;
		if (boardId) {
			this.mmBoardSharesService.openAutoScheduleSnapshot({
				boardId,
				isForTemplateOnboarding: true,
			});
		}
	}

	triggerWizardDataboardIntroTourModalOnSourcesChoiceSelected(): void {
		if (this.tourService.isEligibleForWizardDataboardIntroTour) {
			this.triggerWizardDataboardIntroTourModal();
		}
	}

	// Auto schedule snapshot modal
	triggerTransitionToPublicTemplatesOnSnapshotScheduled(): void {
		if (this.currentUserService.isSignedUpFromTemplate) {
			this.navigationService.transitionToRoute('boards.public-templates');
		}
	}

	// New modals
	private triggerWizardSecondModal(): void {
		const tour = this.tourService.initTour(TourId.WIZARD_SECOND);
		if (tour) {
			tour.start();
		}
	}

	private triggerWizardDataboardIntroTourModal(): void {
		const tour = this.tourService.initTour(TourId.WIZARD_DATABOARD_INTRO);
		if (!tour) {
			return;
		}

		this.navigationService.transitionToRoute('wizard.source').then(() => {
			tour.start();
		});
	}

	// ------- Invited user flow ------

	async handleInvitedUserRedirectToApp(): Promise<void> {
		// show intro video popup when visiting home next
		this.userSettingsService.showInvitedUserIntroVideo = true;

		const firstBoard = this.mmBoardsService.normalBoardModels[0];

		let metricSetups: MetricSetup[] = [];
		if (firstBoard) {
			await this.mmBoardsService.requestBoardData(firstBoard.id!, true);
			metricSetups = this.getMetricSetupsFromBoard([firstBoard]);
		} else {
			this.mmMetricsService.metricsByPopularityConnected.some((m) => {
				const metricSetup = new MetricSetup(m.metricData!);
				if (metricSetup.isFilledSingleOutput) {
					metricSetups.push(metricSetup);
				}
				return metricSetups.length === 3;
			});
		}

		this.markCompleted();
		await this.addMetricsToHome(metricSetups);

		// TODO: board/report flows: favorite board/report, pin report
		// TODO: board/report flows: favorite board/report, pin report (M3: https://app.asana.com/1/7582066485656/project/8014827585025/task/1211908013781398?focus=true)

		// Invited users join an account the owner already set up, so nothing is held or resumed.
		this.aiChatService.openGenieWithOnboardingAnswers(
			"I'm new here. Ask me a few questions to understand my needs, then guide me from here.",
			false,
			false,
		);
	}

	// ------- Marketplace skill ------
	// The website's "Use in Databox" sets comma-separated skill slugs in a .databox.com cookie.

	private isFinishingMarketplaceSkillOnboarding = false;

	get marketplaceSkillSlugs(): string[] {
		const slugs = (Cookies.get(MARKETPLACE_SKILL_COOKIE) ?? '')
			.split(',')
			.map((slug) => slug.trim())
			.filter((slug) => MARKETPLACE_SKILL_SLUG_PATTERN.test(slug));

		return [...new Set(slugs)];
	}

	get isEligibleForMarketplaceSkillFlow(): boolean {
		return (
			this.marketplaceSkillSlugs.length > 0 &&
			!this.templateId &&
			this.currentUserService.isOwner
		);
	}

	// Kept only as a record of which skills the account signed up with.
	recordMarketplaceSkillSignup(): void {
		if (this.isEligibleForMarketplaceSkillFlow) {
			void this.userSettingsService.save(
				UserSettingsKey.MARKETPLACE_SKILL_SIGNUP,
				this.marketplaceSkillSlugs.join(','),
			);
		}
	}

	// The cookie is cleared after every attempt, so failing slugs don't retry on every load.
	async installMarketplaceSkills(): Promise<string[]> {
		const slugs = this.marketplaceSkillSlugs;
		if (!slugs.length) {
			return [];
		}

		if (!this.aiChatService.isAiChatAvailable) {
			this.clearMarketplaceSkill();
			this.infolayerService.warning(
				`You don't have access to install the ${this.describeSkills(slugs)} from Skills marketplace.`,
			);
			return [];
		}

		// Popups close on route change; both callers have a transition pending or about to start.
		if (!this.billingService.features.aiSkillsMarketplaceEnabled) {
			this.clearMarketplaceSkill();
			this.navigationService.router.one('routeDidChange', () => {
				this.billingService.handleLimitations(BillingFeature.AI_SKILLS_MARKETPLACE);
			});
			return [];
		}

		const results = await Promise.allSettled(
			slugs.map(async (slug) => this.aiChatService.installMarketplaceSkill(slug)),
		);
		this.clearMarketplaceSkill();

		const failedSlugs: string[] = [];
		results.forEach((result, index) => {
			if (result.status === 'rejected') {
				const slug = slugs[index]!;
				failedSlugs.push(slug);
				this.loggerService.logError('[ONBOARDING] Marketplace skill install failed', {
					slug,
					error: result.reason,
				});
			}
		});

		if (failedSlugs.length) {
			this.infolayerService.error(
				`The ${this.describeSkills(failedSlugs)} couldn't be installed.`,
			);
		}

		return slugs.filter((slug) => !failedSlugs.includes(slug));
	}

	// `**/slug**` is how Genie serializes a skill mention; its composer seeds it as a skill chip.
	openGenieWithMarketplaceSkillPrompt(slug: string): void {
		this.aiChatService.onStartChat(
			`I’ve downloaded the **/${slug}** skill from Skills marketplace. Let’s run the skill.`,
			ChatPromptMode.PREFILL,
		);
	}

	// markCompleted() runs before the cookie is cleared: its tracking reads the experiment origin.
	async finishMarketplaceSkillOnboarding(): Promise<void> {
		if (this.isFinishingMarketplaceSkillOnboarding) {
			return;
		}
		this.isFinishingMarketplaceSkillOnboarding = true;

		this.markCompleted();

		const [firstInstalledSlug] = await this.installMarketplaceSkills();
		if (firstInstalledSlug) {
			this.openGenieWithMarketplaceSkillPrompt(firstInstalledSlug);
		} else {
			this.redirectToGenie();
		}
	}

	// Removal must use the website's domain: .databox.com in production, none locally.
	private clearMarketplaceSkill(): void {
		Cookies.remove(MARKETPLACE_SKILL_COOKIE, {
			domain: this.accountService.cookieAuthSID?.domain,
		});
	}

	private describeSkills(slugs: string[]): string {
		return slugs.length === 1 ? `${slugs[0]} skill` : `${slugs.join(', ')} skills`;
	}

	// ------- Tracking ------

	origin = 'onboarding';

	getType(sourceTypes: SourceType[]) {
		let type = 'custom_API';

		sourceTypes.some((sourceType) => {
			let match = true;

			switch (sourceType.category) {
				case SourceTypeCategory.CLOUD:
					type = 'cloud';
					break;
				case SourceTypeCategory.SPREADSHEET:
					type = 'sheets';
					break;
				case SourceTypeCategory.DATABASE:
					type = 'database';
					break;
				default:
					match = false;
					break;
			}

			return match;
		});

		return type;
	}
}

declare module '@ember/service' {
	interface Registry {
		onboarding: OnboardingService;
	}
}
